import { createLogto, useLogto, type LogtoConfig } from '@logto/vue';
import { runtimeConfig } from './runtime.ts';

/**
 * Authorization code with PKCE, per the contract.
 *
 * The token is used to authenticate, never to decide what someone may see: the
 * `roles` claim is coarse (fan, staff, admin) and fine-grained access always
 * comes from the entitlements API. Route guards ask the API; they do not
 * inspect the token.
 */
/**
 * Read when the app is created, which is after `loadRuntimeConfig()` — so this
 * is the deployment's own endpoint and application id, not whatever was baked
 * into the bundle at build time. See lib/runtime.ts.
 */
export function logtoConfigFor(): LogtoConfig {
  const config = runtimeConfig();
  return {
    endpoint: config.logtoEndpoint,
    appId: config.logtoAppId,
    resources: [config.apiAudience].filter(Boolean),
    scopes: ['auditions:apply', 'auditions:adjudicate', 'auditions:review', 'auditions:manage'],
  };
}

export const logto = createLogto;

let tokenGetter: (() => Promise<string | undefined>) | null = null;

/** Registered once at startup so the API client need not know about Logto. */
export function registerTokenGetter(fn: () => Promise<string | undefined>): void {
  tokenGetter = fn;
}

/**
 * Asking Logto for a token can need a round trip of its own — a refresh
 * against the auth server — and that request is the SDK's, not ours, so it has
 * no deadline we can set. Without this, a stalled refresh on a phone holds
 * every call that wanted a token, for ever.
 *
 * Giving up returns no token rather than throwing: the call then goes out
 * unauthenticated and comes back 401, which is a message on the screen and a
 * sign-in button, instead of a page that never finishes.
 */
const TOKEN_TIMEOUT_MS = 15_000;

export async function getAccessToken(): Promise<string | undefined> {
  if (!tokenGetter) return undefined;
  try {
    return await Promise.race([
      tokenGetter(),
      new Promise<undefined>((resolve) => { setTimeout(() => { resolve(undefined); }, TOKEN_TIMEOUT_MS); }),
    ]);
  } catch {
    return undefined;
  }
}

export { useLogto };
